import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Check, X, HelpCircle, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { albumsApi } from '../../api/albums';
import styles from './MyPhotosReviewStrip.module.css';

export interface ReviewPhoto {
  _id: string;
  thumbnailUrl: string | null;
  originalUrl: string | null;
  similarity: number;
}

interface Props {
  albumId: string;
  photos: ReviewPhoto[];
  /** Confirmed ✓ → parent adds it to the My Photos grid + count. */
  onConfirmed: (photoId: string) => void;
  /** Rejected ✗ → parent just drops it. */
  onRejected: (photoId: string) => void;
}

/**
 * "Are these you?" — the human-in-the-loop review strip. Surfaces borderline
 * face-match candidates (review band) as a horizontally-scrollable row of
 * clay cards; each gets a ✓ (yes, add + teach the template) or ✗ (no, hide).
 *
 * Tapping a card opens a full-screen viewer so the whole face is visible before
 * deciding — a card thumbnail is often too small to judge identity confidently.
 *
 * Mobile-first: single-thumb reachable, big touch targets, snap scrolling,
 * optimistic removal. Confirmed/rejected cards animate out; when the strip
 * empties it collapses away entirely.
 */
export function MyPhotosReviewStrip({ albumId, photos, onConfirmed, onRejected }: Props) {
  const reduceMotion = useReducedMotion();
  const [items, setItems] = useState<ReviewPhoto[]>(photos);
  // Full-screen viewer: index into `items`, or null when closed.
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // Keep local list in sync when the parent reloads My Photos.
  useEffect(() => setItems(photos), [photos]);

  // Keep the viewer index valid as items are resolved out from under it.
  useEffect(() => {
    if (viewerIndex === null) return;
    if (items.length === 0) setViewerIndex(null);
    else if (viewerIndex >= items.length) setViewerIndex(items.length - 1);
  }, [items.length, viewerIndex]);

  // Lock body scroll while the full-screen viewer is open (matches Modal /
  // AlbumPhotoViewer). Without this the page behind stays scrollable.
  useEffect(() => {
    if (viewerIndex === null) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [viewerIndex]);

  const decide = useCallback(
    (photoId: string, decision: 'confirm' | 'reject') => {
      // Fully optimistic: resolve the UI instantly (remove the card, advance
      // the viewer, update the parent grid/count) and let the network call run
      // in the background. Only a failure rolls the strip back — a rare blip
      // otherwise self-heals on the next My Photos load, so we never make the
      // user wait on the request.
      const snapshot = items;
      setItems((list) => list.filter((p) => p._id !== photoId));
      if (decision === 'confirm') onConfirmed(photoId);
      else onRejected(photoId);

      albumsApi.confirmMyPhoto(albumId, photoId, decision).catch(() => {
        setItems(snapshot); // put the card back
        toast.error('Could not save — try again');
      });
    },
    [albumId, items, onConfirmed, onRejected],
  );

  if (items.length === 0) return null;

  const viewerPhoto = viewerIndex !== null ? items[viewerIndex] : null;

  return (
    <motion.section
      className={styles['strip']}
      initial={false}
      animate={{ opacity: 1 }}
      aria-label="Review possible photos of you"
    >
      <header className={styles['header']}>
        <span className={styles['header-icon']} aria-hidden="true">
          <HelpCircle size={18} />
        </span>
        <div className={styles['header-text']}>
          <h3 className={styles['title']}>Are these you?</h3>
          <p className={styles['subtitle']}>
            Tap a photo to see it full-screen, then confirm. This teaches us your face so
            future matches get better.
          </p>
        </div>
        <span className={styles['count']}>{items.length}</span>
      </header>

      <div className={styles['scroller']} role="list">
        <AnimatePresence initial={false} mode="popLayout">
          {items.map((photo, index) => {
            return (
              <motion.div
                key={photo._id}
                role="listitem"
                className={styles['card']}
                layout={!reduceMotion}
                initial={reduceMotion ? false : { opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.8, transition: { duration: 0.18 } }}
                transition={{ type: 'spring', stiffness: 500, damping: 34 }}
              >
                <button
                  type="button"
                  className={styles['photo-wrap']}
                  onClick={() => setViewerIndex(index)}
                  aria-label="View full screen to check if it's you"
                >
                  {photo.thumbnailUrl ? (
                    <img
                      src={photo.thumbnailUrl}
                      alt="Possible match"
                      className={styles['photo']}
                      loading="lazy"
                      draggable={false}
                    />
                  ) : (
                    <div className={styles['photo-fallback']} aria-hidden="true" />
                  )}
                  <span className={styles['expand-hint']} aria-hidden="true">
                    <Maximize2 size={14} />
                  </span>
                </button>

                <div className={styles['actions']}>
                  <button
                    type="button"
                    className={`${styles['action']} ${styles['reject']}`}
                    onClick={() => decide(photo._id, 'reject')}
                    aria-label="Not me — hide this photo"
                  >
                    <X size={20} strokeWidth={2.75} />
                  </button>
                  <button
                    type="button"
                    className={`${styles['action']} ${styles['confirm']}`}
                    onClick={() => decide(photo._id, 'confirm')}
                    aria-label="Yes, this is me — add to my photos"
                  >
                    <Check size={20} strokeWidth={2.75} />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Full-screen viewer for a careful "is this me?" decision. Portaled to
          document.body: framer-motion ancestors apply transforms, which would
          otherwise trap a position:fixed overlay inside the album page. */}
      {createPortal(
      <AnimatePresence>
        {viewerPhoto && viewerIndex !== null && (
          <motion.div
            className={styles['viewer']}
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            role="dialog"
            aria-modal="true"
            aria-label="Is this you?"
            onClick={() => setViewerIndex(null)}
          >
            <div className={styles['viewer-topbar']} onClick={(e) => e.stopPropagation()}>
              <span className={styles['viewer-counter']}>
                {viewerIndex + 1} / {items.length}
              </span>
              <button
                type="button"
                className={styles['viewer-close']}
                onClick={() => setViewerIndex(null)}
                aria-label="Close"
              >
                <X size={22} />
              </button>
            </div>

            <div className={styles['viewer-stage']} onClick={(e) => e.stopPropagation()}>
              {viewerIndex > 0 && (
                <button
                  type="button"
                  className={`${styles['viewer-nav']} ${styles['viewer-prev']}`}
                  onClick={() => setViewerIndex((i) => (i === null ? i : Math.max(0, i - 1)))}
                  aria-label="Previous"
                >
                  <ChevronLeft size={26} />
                </button>
              )}
              <img
                src={viewerPhoto.originalUrl ?? viewerPhoto.thumbnailUrl ?? ''}
                alt="Is this you?"
                className={styles['viewer-img']}
                draggable={false}
              />
              {viewerIndex < items.length - 1 && (
                <button
                  type="button"
                  className={`${styles['viewer-nav']} ${styles['viewer-next']}`}
                  onClick={() => setViewerIndex((i) => (i === null ? i : Math.min(items.length - 1, i + 1)))}
                  aria-label="Next"
                >
                  <ChevronRight size={26} />
                </button>
              )}
            </div>

            <div className={styles['viewer-actions']} onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className={`${styles['viewer-btn']} ${styles['reject']}`}
                onClick={() => decide(viewerPhoto._id, 'reject')}
              >
                <X size={20} strokeWidth={2.75} />
                Not me
              </button>
              <button
                type="button"
                className={`${styles['viewer-btn']} ${styles['confirm']}`}
                onClick={() => decide(viewerPhoto._id, 'confirm')}
              >
                <Check size={20} strokeWidth={2.75} />
                Yes, it's me
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body,
      )}
    </motion.section>
  );
}
